import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Flame, Mail, Lock, AlertCircle, Eye, EyeOff, Download, X, ArrowLeft } from 'lucide-react';
import Button from '../components/Button';
import Input from '../components/Input';
import { MARKETING_SITE_URL } from '../config/api';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setShowInstallBanner(false);
    }
    setDeferredPrompt(null);
  };

  const dismissBanner = () => {
    setShowInstallBanner(false);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(formData.name, formData.password);
      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.message || 'Nom ou mot de passe incorrect');
      }
    } catch (err) {
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 relative overflow-hidden">
      {/* Minimal background pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] opacity-50"></div>
      
      {/* Top bar with back link */}
      <div className="relative z-20 p-3 md:p-4">
        <a 
          href={MARKETING_SITE_URL}
          className="inline-flex items-center gap-1.5 text-xs md:text-sm text-gray-600 hover:text-primary-600 font-medium transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5 md:w-4 md:h-4" />
          Retour au site web
        </a>
      </div>

      {/* Main content - centered */}
      <div className="flex-1 flex items-center justify-center px-4 pb-16 md:pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full relative z-10"
        >
          {/* Logo - smaller on mobile */}
          <div className="text-center mb-4 md:mb-8">
            <motion.div 
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
              className="inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl bg-red-600 mb-2 md:mb-4 shadow-lg"
            >
              <Flame className="w-7 h-7 md:w-9 md:h-9 text-white" />
            </motion.div>
            <h1 className="text-xl md:text-3xl font-bold text-gray-900 mb-1 md:mb-2 tracking-tight">PFE</h1>
            <p className="text-gray-600 text-xs md:text-sm">Gestion des Équipements de Sécurité Incendie</p>
          </div>

          {/* Login Form - compact padding on mobile */}
          <div className="bg-white rounded-xl md:rounded-2xl shadow-sm p-5 md:p-8 border border-gray-200">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">Connexion</h2>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-3 md:mb-4 p-3 md:p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2 md:space-x-3"
              >
                <AlertCircle className="w-4 h-4 md:w-5 md:h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs md:text-sm text-red-700">{error}</p>
              </motion.div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4">
              <Input
                label="Email ou nom d'utilisateur"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="email@entreprise.com ou nom du compte"
                icon={Mail}
                required
              />

              <div className="relative">
                <Input
                  label="Mot de passe"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  icon={Lock}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-[38px] p-1 text-gray-500 hover:text-gray-700 transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>

              <div className="flex items-center justify-end text-xs md:text-sm">
                <Link
                  to="/reset-password"
                  className="text-primary-600 hover:text-primary-700 font-medium"
                >
                  Mot de passe oublié ?
                </Link>
              </div>

              <Button
                type="submit"
                variant="primary"
                className="w-full"
                loading={loading}
                disabled={loading}
              >
                Se connecter
              </Button>
            </form>

            {/* Footer inside card */}
            <p className="text-center text-xs text-gray-500 mt-4 md:mt-6">
              © 2025 PETROFEU ÉQUIPEMENTS
            </p>
          </div>
        </motion.div>
      </div>
      
      {/* PWA Install Banner - Compact on mobile, larger on desktop */}
      <AnimatePresence>
        {showInstallBanner && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50"
          >
            {/* Mobile version - compact */}
            <div className="md:hidden bg-gradient-to-r from-red-600 to-red-700 rounded-xl shadow-2xl p-3 text-white">
              <button
                onClick={dismissBanner}
                className="absolute top-2 right-2 p-1 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-9 h-9 bg-white rounded-lg flex items-center justify-center">
                  <Flame className="w-5 h-5 text-red-600" />
                </div>
                <span className="font-semibold text-sm flex-1">Installer l'App</span>
                <button
                  onClick={handleInstall}
                  className="flex-shrink-0 bg-white text-red-600 p-2 rounded-lg hover:bg-red-50 transition-colors"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Desktop version - larger */}
            <div className="hidden md:block bg-gradient-to-r from-red-600 to-red-700 rounded-2xl shadow-2xl p-5 text-white">
              <button
                onClick={dismissBanner}
                className="absolute top-3 right-3 p-1.5 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-lg">
                  <Flame className="w-8 h-8 text-red-600" />
                </div>
                <div className="flex-1 pr-6">
                  <h3 className="font-bold text-lg">Installer l'Application</h3>
                  <p className="text-sm text-red-100 mt-1">Accédez rapidement à PFE depuis votre écran d'accueil</p>
                  <button
                    onClick={handleInstall}
                    className="mt-3 flex items-center gap-2 bg-white text-red-600 px-4 py-2 rounded-lg font-semibold text-sm hover:bg-red-50 transition-colors shadow-md"
                  >
                    <Download className="w-4 h-4" />
                    Installer maintenant
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Login;
